import React, { useState } from 'react';

import { ErrorMessage } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import TextField from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';

import { Folder } from '@qality/common-model';
import { useTestCaseRepositoryFoldersContext } from '@qality/common-pages/src/TestCaseRepositoryPage/TestCaseRepositoryFoldersProvider';
import { FlagService, FolderService } from '@qality/common-service';
import { useOverflowObserver } from '@qality/common-utils';

import {
  ContainerStyled,
  ErrorMessageStyled,
  FolderNameStyled,
  WrapperStyled,
} from './EditableFolderName.styled';

type Props = {
  folder: Folder;
  elementAfter?: React.ReactNode;
};

const CHARACTER_LIMIT = 255;
const LENGTH_ERROR = 'Folder name must be between 1 and 255 characters long';

export default function EditableFolderName({
  folder,
  elementAfter,
}: Props): JSX.Element {
  const [isValid, setIsValid] = useState(true);
  const { updateFolder } = useTestCaseRepositoryFoldersContext();
  const { ref, isOverflowing } = useOverflowObserver();

  const handleConfirm = (value: string) => {
    if (!isValid || value === folder.name) {
      return false;
    }

    try {
      FolderService.updateFolder({
        id: folder.id,
        name: value,
      });

      updateFolder({ ...folder, name: value });

      return true;
    } catch (error) {
      FlagService.createErrorFlag(
        'Error',
        `We could not edit folder's name. Please try again. If the problem persists, feel free to contact our support.`
      );
      return false;
    }
  };

  const validate = (value: string): undefined | string => {
    const isStatusNameEmpty = value.trim().length === 0;

    if (isStatusNameEmpty || value.length > CHARACTER_LIMIT) {
      setIsValid(false);
      return LENGTH_ERROR;
    }

    setIsValid(true);
    return undefined;
  };

  const getReadView = () => {
    return (
      <Tooltip content={isOverflowing && folder.name}>
        <WrapperStyled>
          <FolderNameStyled innerRef={ref}>{folder.name}</FolderNameStyled>
          {elementAfter}
        </WrapperStyled>
      </Tooltip>
    );
  };

  if (!folder.canEdit) return getReadView();

  return (
    <ContainerStyled>
      <InlineEdit
        key={folder.id}
        readViewFitContainerWidth
        defaultValue={folder.name}
        onConfirm={handleConfirm}
        onCancel={() => {
          setIsValid(true);
        }}
        validate={validate}
        readView={() => getReadView()}
        editView={({ ...fieldProps }) => (
          <>
            <WrapperStyled>
              <TextField
                {...fieldProps}
                isInvalid={!isValid}
                autoFocus
                autoComplete='off'
                testId='folder-edit-text-field'
              />
            </WrapperStyled>

            {fieldProps.errorMessage && (
              <ErrorMessageStyled>
                <ErrorMessage> {fieldProps.errorMessage}</ErrorMessage>
              </ErrorMessageStyled>
            )}
          </>
        )}
      />
    </ContainerStyled>
  );
}
